import { useCallback, useEffect, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Popover } from 'react-bootstrap';
import {
  Icon,
  Button,
  FileUploadBanner,
  FileUploadBannerType,
  OverlayTrigger,
} from '@pwc/pwc-deals-ui';
import { Buffer } from 'buffer';
import { ReportingApiClient } from 'common/services/httpClient';
import { viewAnalysisExcelTablePath } from 'common/services/urlService';
import { getStructuredAnalysisPassword } from 'common/engine/services/structuredAnalysisService';
import { isUSTerritory } from 'common/helpers/projectInitUtil';
import {
  getFileDownload,
  resetFileDownload,
  setFileDownloadError,
  setFileDownloadLoading,
} from 'common/engine/reducers/structuredDocumentDescriptionSlice';

import './StructuredAnalysisFileDownload.scss';
import { APIResponse } from '@pwc/pwc-deals-web-common';
import { getProjectId } from 'features/project/projectSlice';

enum PasswordActionType {
  SET_PASSWORD = 'SET_PASSWORD',
  TOGGLE_SHOW_PASSWORD = 'TOGGLE_SHOW_PASSWORD',
}

interface PasswordAction {
  type: PasswordActionType;
  payload?: string;
}

interface PasswordState {
  password: string;
  showPassword: boolean;
}
function passwordReducer(state: PasswordState, action: PasswordAction) {
  const { type, payload } = action;
  switch (type) {
    case 'SET_PASSWORD': {
      return {
        ...state,
        password: payload || '',
      };
    }
    case 'TOGGLE_SHOW_PASSWORD': {
      return {
        ...state,
        showPassword: !state.showPassword,
      };
    }
    default: {
      throw new Error(`Unhandled type: ${action.type}`);
    }
  }
}

// TODO: Fix these FixMeLater when the APIs are migrated
type FixMeLaterGetFileDownloadSelector = {
  loading: boolean;
  hasError: boolean;
  message: string;
};
type FixMeLaterReportingApiClisntGetDownloadUrl = APIResponse<{
  fileStream: string;
  fileName: string;
}>;
type FixMeLaterGetStructuredAnalysisPassword = {
  password: string;
};

interface StructuredAnalysisFileDownloadProps {
  title: string;
  contentThreadId: number;
  isShared: boolean;
  versionNumber?: string;
  isThirdPartyMember: boolean;
}

const StructuredAnalysisFileDownload = ({
  title,
  contentThreadId,
  isShared,
  versionNumber,
  isThirdPartyMember,
}: StructuredAnalysisFileDownloadProps) => {
  const projectId = useSelector(getProjectId);
  const fileDownloadStatus = useSelector(
    getFileDownload
  ) as FixMeLaterGetFileDownloadSelector;
  const [{ password, showPassword }, passwordDispatch] = useReducer(
    passwordReducer,
    {
      password: '',
      showPassword: false,
    }
  );

  const fetchPassword = useCallback(async () => {
    if (!projectId || !contentThreadId || !versionNumber) return;
    if (isUSTerritory) return;
    try {
      const { password: fetchedPassword } =
        (await getStructuredAnalysisPassword(
          projectId,
          contentThreadId,
          isShared ? 'shared' : 'latest'
        )) as FixMeLaterGetStructuredAnalysisPassword;
      if (fetchedPassword) {
        passwordDispatch({
          type: PasswordActionType.SET_PASSWORD,
          payload: fetchedPassword,
        });
      }
    } catch (error) {
      passwordDispatch({ type: PasswordActionType.SET_PASSWORD, payload: '' });
    }
  }, [projectId, contentThreadId, versionNumber, isShared]);

  useEffect(() => {
    fetchPassword()
      .then(() => null)
      .catch(() => null);
  }, [contentThreadId, fetchPassword, versionNumber]);

  const dispatch = useDispatch();

  const closeDownloadErrorBanner = () => {
    dispatch(resetFileDownload());
  };

  const handleDownload = async () => {
    try {
      if (fileDownloadStatus.loading) {
        return;
      }

      dispatch(setFileDownloadLoading());
      const downloadUrl = viewAnalysisExcelTablePath(
        projectId,
        contentThreadId,
        isShared ? 'shared' : 'latest'
      );
      const response = (await ReportingApiClient.get(
        downloadUrl
      )) as FixMeLaterReportingApiClisntGetDownloadUrl;
      if (!response.data)
        throw new Error('NO data in response for download URL');
      const { fileStream, fileName } = response.data;
      const url = window.URL.createObjectURL(
        new Blob([Buffer.from(fileStream, 'base64')])
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      dispatch(resetFileDownload());
    } catch (error) {
      dispatch(
        setFileDownloadError(`Error downloading ${title}. Please try again`)
      );
    }
  };

  const handleShowPassword = () => {
    passwordDispatch({
      type: PasswordActionType.TOGGLE_SHOW_PASSWORD,
    });
  };

  const handleCopyPassword = async () => {
    await navigator.clipboard.writeText(password);
  };

  useEffect(() => {
    if (!fileDownloadStatus.hasError && !fileDownloadStatus.loading) {
      dispatch(resetFileDownload());
    }
  }, [dispatch, fileDownloadStatus.hasError, fileDownloadStatus.loading]);

  useEffect(() => {
    return () => {
      dispatch(resetFileDownload());
    };
  }, [dispatch]);

  if (!isUSTerritory && !password) return null;
  return (
    <>
      <div className="structure-analysis-file-download">
        <div>
          {!isThirdPartyMember && (
            <Button
              className="ui-button button-link download-button"
              onClick={handleDownload}
              data-testid="structure-analysis-file-download"
              aria-label="download"
              disabled={fileDownloadStatus.loading}
              icon={undefined}
              iconClasses={undefined}
            >
              <Icon
                name="download"
                className="outline icon-orange download-icon"
                data-testid="file-download-icon"
                width="18"
              />
              {fileDownloadStatus.loading
                ? 'Downloading...'
                : 'Download table (.xlsx)'}
            </Button>
          )}
          {!isUSTerritory && password && (
            <Button
              className={`${
                !showPassword ? 'show-password-button' : 'hide-password-button'
              } ui-button button-link`}
              onClick={handleShowPassword}
              data-testid="structure-analysis-show-password"
              aria-label={`${!showPassword ? 'show' : 'hide'}-password`}
              icon={undefined}
              iconClasses={undefined}
            >
              <Icon
                name="locked"
                className={`${
                  !showPassword ? 'outline' : 'fill'
                }  password-icon icon-orange`}
                data-testid="show-password-icon"
                width="18"
              />
              {!showPassword ? 'Show password' : 'Hide password'}
            </Button>
          )}
        </div>
        {!isUSTerritory && showPassword && password && (
          <div className="password-field-wrapper">
            <div className="password-field">
              <span>{password}</span>
              <OverlayTrigger
                rootClose
                trigger={['hover', 'focus']}
                placement="top"
                overlay={
                  <Popover
                    content
                    bsPrefix="popoverMenu"
                    className="sa-password-popover"
                    id="password-popover"
                  >
                    <span>Copy password</span>
                  </Popover>
                }
              >
                <button
                  type="button"
                  className="icon-transparent"
                  aria-label="Download report"
                  data-testid="password-field-copy-password"
                  onClick={handleCopyPassword}
                >
                  <Icon name="copy" className="outline" width="18" />
                </button>
              </OverlayTrigger>
            </div>
          </div>
        )}
      </div>
      <FileUploadBanner
        type={FileUploadBannerType.ERROR}
        isFileFormatError={false}
        bannerMessage={fileDownloadStatus.message}
        show={fileDownloadStatus.hasError}
        onCloseBanner={closeDownloadErrorBanner}
        isDismissable
      />
    </>
  );
};

export default StructuredAnalysisFileDownload;
